"use client";

import * as React from "react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useDeleteVenue } from "@/hooks/use-venues";
import { toast } from "sonner";

interface DeleteVenueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  venueId: string;
  venueName: string;
  onSuccess?: () => void;
}

export function DeleteVenueDialog({
  open,
  onOpenChange,
  venueId,
  venueName,
  onSuccess,
}: DeleteVenueDialogProps) {
  const deleteMutation = useDeleteVenue();

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    deleteMutation.mutate(venueId, {
      onSuccess: () => {
        toast.success(`Venue "${venueName}" deleted successfully!`);
        onOpenChange(false);
        onSuccess?.();
      },
      onError: (error: Error) => {
        const errorMessage =
          typeof error === "string"
            ? error
            : error?.message || "Failed to delete venue";
        toast.error(errorMessage);
      },
    });
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently delete the venue "{venueName}". This action
            cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteMutation.isPending}>
            Cancel
          </AlertDialogCancel>
          <Button
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            variant="destructive"
          >
            {deleteMutation.isPending ? (
              <>
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
